import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { api, fmtMoney, fmtPct } from "../lib/api";
import { toast } from "sonner";

export default function CatalogPicker({ open, onOpenChange, bond, onSaved }) {
  const [shares, setShares] = useState("");
  const [purchase_price, setPrice] = useState("");
  const [purchase_date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && bond?.current_price) setPrice(bond.current_price.toFixed(2));
    if (open) {
      setShares("");
      setNotes("");
      setDate(new Date().toISOString().slice(0, 10));
    }
  }, [open, bond]);

  if (!bond) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/holdings/from-catalog", {
        catalog_id: bond.id,
        shares: Number(shares),
        purchase_price: Number(purchase_price),
        purchase_date,
        notes,
      });
      if (data.duplicate_warning) {
        toast.warning(data.duplicate_warning);
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-white border border-stone px-3 py-2 text-sm font-mono text-graphite focus:border-paars-dark focus:outline-none";
  const labelCls = "text-[10px] tracking-[0.2em] uppercase text-graphite/55 font-bold block mb-1.5";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!rounded-none bg-white border border-stone max-w-lg text-graphite p-0">
        <DialogHeader className="px-6 py-4 border-b border-stone">
          <DialogTitle className="text-base font-mono uppercase tracking-widest text-paars-dark">
            // ADD TO PORTFOLIO
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pt-4 pb-2 bg-paars/10 border-b border-stone">
          <div className="flex items-baseline gap-2 mb-1">
            <div className="text-xl font-mono font-bold text-paars-dark">{bond.ticker}</div>
            <div className="text-[10px] tracking-widest uppercase text-graphite/55 font-mono">{bond.coupon_rate}% · matures {bond.maturity_date}</div>
          </div>
          <div className="text-xs text-graphite/70 truncate">{bond.fullname}</div>
          <div className="mt-2 grid grid-cols-3 gap-3 text-[11px] font-mono">
            <div><span className="text-graphite/45">YIELD ON PAR </span><span className="text-paars-dark">{fmtPct(bond.yield_on_par)}</span></div>
            <div><span className="text-graphite/45">PER PAYMENT </span><span className="text-groen-dark">{fmtMoney(bond.amount_per_distribution)}</span></div>
            <div><span className="text-graphite/45">PRICE </span><span className="text-graphite">{bond.current_price ? fmtMoney(bond.current_price) : "—"}</span></div>
          </div>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Shares *</label>
              <input data-testid="picker-shares" required type="number" step="0.0001" value={shares} onChange={(e)=>setShares(e.target.value)} className={inputCls} autoFocus />
            </div>
            <div>
              <label className={labelCls}>Purchase Price *</label>
              <input data-testid="picker-price" required type="number" step="0.0001" value={purchase_price} onChange={(e)=>setPrice(e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Purchase Date *</label>
            <input required type="date" value={purchase_date} onChange={(e)=>setDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <input value={notes} onChange={(e)=>setNotes(e.target.value)} className={inputCls} placeholder="(optional)" />
          </div>

          {shares && purchase_price && (
            <div className="border border-stone bg-stone/30 p-3 text-[11px] font-mono space-y-0.5">
              <div className="text-graphite/45 tracking-widest uppercase text-[9px] mb-1">SUMMARY</div>
              <div>Cost basis: <span className="text-graphite">{fmtMoney(Number(shares) * Number(purchase_price))}</span></div>
              <div>Annual income: <span className="text-groen-dark">{fmtMoney(Number(shares) * bond.par_value * bond.coupon_rate / 100)}</span></div>
              <div>YOC: <span className="text-paars-dark">{purchase_price > 0 ? fmtPct(bond.par_value * bond.coupon_rate / Number(purchase_price)) : "—"}</span></div>
            </div>
          )}

          <DialogFooter className="!flex !justify-end gap-2 pt-2">
            <button type="button" onClick={()=>onOpenChange(false)} className="px-5 py-2.5 border border-stone text-graphite/80 text-sm hover:bg-stone/50">Cancel</button>
            <button data-testid="picker-save" type="submit" disabled={saving} className="px-5 py-2.5 bg-paars text-graphite font-semibold text-sm hover:bg-paars-light disabled:opacity-50">
              {saving ? "ADDING..." : "ADD POSITION"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
